// src/pages/MeetingRoom/hooks/useMeetingFeedback.js
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { startLoading, stopLoading } from "../../../store/uiSlice";
import { setCompletedPlan, clearCompletedPlan as clearCompletedPlanAction } from "../../../store/trainingSlice";
import { setEntitlements, setUserStreak } from "../../../store/userSlice";
import { markMeetingStarted, markMeetingCompleted, submitFeedback, uploadMeetingRecording } from "../../../api/meetingApi";
import { completeTraining } from "../../../api/trainingApi";
import { consumeRealtimeUsage, getRealtimeEntitlements } from "../../../api/realtimeApi";
import useSessionRecording from "./useSessionRecording";
import { getTranscriptionLanguageCode } from "../../../config/meetingOptions";
import {
  isRealtimeUpgradeRequired,
  realtimeUpgradeMessage,
} from "../../../utils/realtimeUpgradeState";

/**
 * Manages training + feedback logic.
 * Handles session start, session end, and feedback submission.
 */
export default function useMeetingFeedback({
  toast,
  trainingSession,
  meetingData,
  safeMeetingId,
  elapsed,
  convRef,
  setConversation,
  connect,
  disconnect,
  setMicEnabled,
  setSessionActive,
  sessionActive,
  setIsMuted,
  markAsStarted,
  resetTimer,
  toggleMic,
  isMuted,
  setEndProposed,
  resetInstructions,
  getLocalStream,
  getRemoteStream,
}) {
  const dispatch = useDispatch();
  const currentEntitlements = useSelector((s) => s.user?.data?.entitlements || null);
  const [feedbackData, setFeedbackData] = useState(null);
  const [feedbackError, setFeedbackError] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [completedPlan, setCompletedPlanLocal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const lastPersistedElapsedSecondsRef = useRef(0);
  const usageSyncInFlightRef = useRef(false);
  const currentElapsedRef = useRef(0);
  const latestRecordingRef = useRef(null);
  const { startRecording, stopRecording } = useSessionRecording();

  useEffect(() => {
    currentElapsedRef.current = Number(elapsed || 0);
  }, [elapsed]);

  const persistUsageDelta = useCallback(async (targetElapsedSeconds, { silent = false } = {}) => {
    const totalSeconds = Math.max(0, Number(targetElapsedSeconds || 0));
    const deltaSeconds = Math.max(0, totalSeconds - lastPersistedElapsedSecondsRef.current);
    if (deltaSeconds <= 0 || usageSyncInFlightRef.current) return;

    usageSyncInFlightRef.current = true;
    try {
      const usage = await consumeRealtimeUsage({
        minutes: Number((deltaSeconds / 60).toFixed(4)),
        meetingId: safeMeetingId,
        trainingPlanId: trainingSession?.id || null,
      });
      lastPersistedElapsedSecondsRef.current = totalSeconds;
      if (usage?.entitlements) {
        dispatch(setEntitlements(usage.entitlements));
      }
    } catch (usageErr) {
      if (!silent) {
        const usageErrorMsg =
          usageErr?.response?.data?.error ||
          usageErr?.error ||
          usageErr?.message ||
          "Could not update usage minutes.";
        toast({
          title: "Minutes sync issue",
          description: usageErrorMsg,
          variant: "warning",
        });
      }
    } finally {
      usageSyncInFlightRef.current = false;
    }
  }, [dispatch, safeMeetingId, toast, trainingSession?.id]);

  // ─────────────────────────────
  // 🔹 START SESSION
  // ─────────────────────────────
// Handle Start Session
  const handleStartSession = async () => {
  try {
    lastPersistedElapsedSecondsRef.current = 0;
    setSessionError(null);
    if (typeof resetInstructions === "function") resetInstructions();
    setIsConnecting(true);
    setFeedbackError(null);
    setFeedbackData(null);
    resetTimer();

    setConversation([]);
    convRef.current = [];

    if (typeof setEndProposed === "function") {
      setEndProposed(null); // 🧹 clears “Session ended” + reason
    }

    // ✅ Ensure mic permission
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });

    await connect(
      {
        type: "realtime",
        output_modalities: ["audio"],
        audio: {
          input: {
            transcription: {
              model: "whisper-1",
              language: getTranscriptionLanguageCode(meetingData?.languageLabel),
            },
          },
          output: {
            voice: meetingData?.aiVoice || "cedar",
          },
        },
      },
      stream,
      {
        trainingPlanId: trainingSession?.id || null,
        pushToTalk: Boolean(trainingSession),
      }
    );

    try {
      const entitlements = await getRealtimeEntitlements();
      dispatch(setEntitlements(entitlements));
    } catch (e) {
      console.warn("[Start Session] Could not refresh entitlements:", e);
    }

    try {
      await startRecording({
        localStream: getLocalStream?.() || null,
        remoteStream: getRemoteStream?.() || null,
        getLocalStream,
        getRemoteStream,
      });
    } catch (recordErr) {
      console.warn("[Start Session] Recording could not start:", recordErr);
    }

    // ✅ Mark meeting started in backend
    if (safeMeetingId) {
      await markMeetingStarted(safeMeetingId);
      if (markAsStarted) markAsStarted();
    }

    // Training uses explicit record/send turns; practice remains open-mic.
    setMicEnabled(!trainingSession);
    setSessionActive(true);
    setIsMuted(Boolean(trainingSession));

  } catch (err) {
    console.error("[Start Session] failed", err);
    const message = err?.error || err?.message || "Please check your microphone and try again.";
    if (isRealtimeUpgradeRequired(err)) {
      setSessionError(realtimeUpgradeMessage(err));
      return;
    }

    setSessionError(message);
    toast({
      title: "Could not start session",
      description: message,
      variant: "destructive",
    });
  } finally {
    setIsConnecting(false);
  }
};




  // ─────────────────────────────
  // 🔹 TOGGLE MUTE
  const handleToggleMute = () => toggleMic(!isMuted);
  // ─────────────────────────────
  // 🔹 END SESSION (Feedback/Training)
  // ─────────────────────────────
  const showCompletedPracticeFeedback = useCallback(async (feedback) => {
    const completion = await markMeetingCompleted(safeMeetingId);
    const streak = completion?.streak || null;

    if (streak) dispatch(setUserStreak(streak));
    setFeedbackData({ ...feedback, ...(streak ? { streak } : {}) });
    setFeedbackError(null);
  }, [dispatch, safeMeetingId]);

  const handleEndSession = async ({ force = false, reason = "manual", skipFeedback = false } = {}) => {
    const elapsedAtEnd = Number(elapsed || 0);
    const unpersistedDeltaSeconds = Math.max(0, elapsedAtEnd - lastPersistedElapsedSecondsRef.current);

    if (currentEntitlements && !currentEntitlements.isUnlimited && unpersistedDeltaSeconds > 0) {
      const consumedMinutes = unpersistedDeltaSeconds / 60;
      const nextUsed = Math.max(0, Number(currentEntitlements.usedMinutes || 0) + consumedMinutes);
      const nextRemaining = Math.max(0, Number(currentEntitlements.remainingMinutes || 0) - consumedMinutes);
      dispatch(
        setEntitlements({
          ...currentEntitlements,
          usedMinutes: nextUsed,
          remainingMinutes: nextRemaining,
          canUseRealtime: nextRemaining > 0,
        })
      );
    }

    setSessionActive(false);
    // Disable the mic and stop session
    setMicEnabled(false);

    setIsMuted(false);
    resetTimer();
  
    // Check if the session meets the minimum requirements (30s and min 2 turns)
    const MIN_SECONDS = 30;
    const MIN_TURNS = 2;

      
    const conversation = (convRef.current || []).filter(
      (t) => (t.user && t.user.trim()) || (t.ai && t.ai.trim())
    );

    const syncUsage = async () => {
      await persistUsageDelta(elapsedAtEnd);
    };

    if (skipFeedback) {
      await stopRecording();
      await syncUsage();
      disconnect();
      setIsLoading(false);
      setLoadingMsg("");
      setFeedbackError(null);
      return;
    }
  
    if (!force && (elapsedAtEnd < MIN_SECONDS || conversation.length < MIN_TURNS)) {
      await stopRecording();
      await syncUsage();
      disconnect();
      toast({
        title: trainingSession ? "Training not completed" : "Keep going a little longer",
        description: `Add a bit more conversation to see how well you did.`,
        variant: "warning",
      });
      return;
    }

    if (force && (elapsedAtEnd < MIN_SECONDS || conversation.length < MIN_TURNS)) {
      await stopRecording();
      await syncUsage();
      disconnect();
      toast({
        title: "Session ended",
        description: reason === "entitlement_exhausted"
          ? "Your realtime minutes have reached zero."
          : "Session ended.",
        variant: "warning",
      });
      return;
    }
  
    const capturedRecording = await stopRecording();
    disconnect();
    await syncUsage();
    let uploadedRecording = latestRecordingRef.current;
    const shouldUploadPracticeRecording = !trainingSession;
    if (shouldUploadPracticeRecording && capturedRecording?.blob && safeMeetingId) {
      try {
        uploadedRecording = await uploadMeetingRecording(safeMeetingId, {
          blob: capturedRecording.blob,
          durationSeconds: elapsedAtEnd,
        });
        latestRecordingRef.current = uploadedRecording || null;
      } catch (recordingUploadErr) {
        console.error("Recording upload failed:", recordingUploadErr);
        const recordingUploadMessage =
          recordingUploadErr?.response?.data?.error ||
          recordingUploadErr?.message ||
          "Feedback was generated, but we could not save your audio recording.";
        toast({
          title: "Recording unavailable",
          description: recordingUploadMessage,
          variant: "warning",
        });
      }
    }
    const feedbackPayload = { conversation, recording: uploadedRecording || null };
  
    // Proceed with feedback generation
    try {
      if (trainingSession) {
        setIsLoading(true);
        setLoadingMsg("Grading your training session…");
        dispatch(startLoading({ key: "feedback" }));
  
        const result = await completeTraining(
          trainingSession.id,
          conversation,
          meetingData,
          elapsedAtEnd
        );
        const enriched = {
          ...trainingSession,
          score: result.score,
          strengths: result.strengths,
          areasForImprovement: result.areasForImprovement,
          trainingFeedback: result.feedback,
          completedAt: result.completedAt,
          timeSpentInSeconds: result.timeSpentInSeconds,
        };
        const completed = { plan: enriched, meeting: meetingData, streak: result.streak || null };
        if (result.streak) dispatch(setUserStreak(result.streak));
        dispatch(setCompletedPlan(completed));
        setCompletedPlanLocal(completed);
      } else {
        setIsLoading(true);
        setLoadingMsg("Preparing your results…");
        dispatch(startLoading({ key: "feedback" }));
  
        const feedback = await submitFeedback(safeMeetingId, feedbackPayload);
        await showCompletedPracticeFeedback(feedback);
      }
    } catch (err) {
      console.error("Feedback error:", err);
      setFeedbackError(err.response.data.message || "Couldn’t generate feedback.");
      toast({
        title: "Feedback not generated",
        variant: "warning",
      });
    } finally {
      setIsLoading(false);
      setLoadingMsg("");
      dispatch(stopLoading("feedback"));
    }
  };
  useEffect(() => {
    const retryHandler = async () => {
      try {
        setFeedbackError(null);
        setIsLoading(true);
        setLoadingMsg("Retrying feedback generation…");
  
        const conversation = (convRef.current || []).filter(
          (t) => (t.user && t.user.trim()) || (t.ai && t.ai.trim())
        );
        const feedbackPayload = {
          conversation,
          recording: latestRecordingRef.current || null,
        };
  
        const feedback = await submitFeedback(safeMeetingId, feedbackPayload);
        await showCompletedPracticeFeedback(feedback);
      } catch (err) {
        console.error("Retry failed:", err);
        setFeedbackError(err.response.data.message || "Still couldn’t generate feedback.");
      } finally {
        setIsLoading(false);
        setLoadingMsg("");
      }
    };
  
    window.addEventListener("retry-feedback", retryHandler);
    return () => window.removeEventListener("retry-feedback", retryHandler);
  }, [convRef, safeMeetingId, showCompletedPracticeFeedback]);

  useEffect(() => {
    if (!sessionActive) return undefined;

    const intervalId = setInterval(() => {
      persistUsageDelta(currentElapsedRef.current, { silent: true });
    }, 10000);

    const flushUsage = () => {
      persistUsageDelta(currentElapsedRef.current, { silent: true });
    };

    window.addEventListener("visibilitychange", flushUsage);
    window.addEventListener("pagehide", flushUsage);
    window.addEventListener("beforeunload", flushUsage);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("visibilitychange", flushUsage);
      window.removeEventListener("pagehide", flushUsage);
      window.removeEventListener("beforeunload", flushUsage);
      flushUsage();
    };
  }, [persistUsageDelta, sessionActive]);
  

  // ─────────────────────────────
  // 🔹 CLEAR
  // ─────────────────────────────
  const clearFeedback = () => {
    setFeedbackData(null);
    setFeedbackError(null);
  };

  const clearCompletedPlan = () => {
    dispatch(clearCompletedPlanAction());
    setCompletedPlanLocal(null);
  };

  // ─────────────────────────────
  // 🔹 Return
  // ─────────────────────────────
  return {
    feedbackData,
    feedbackError,
    sessionError,
    isLoading,
    loadingMsg,
    completedPlan,
    handleStartSession,
    handleToggleMute,
    handleEndSession,
    clearFeedback,
    clearCompletedPlan,
    isConnecting,
  };
}
